import { createSlice } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';
import { apiCallBegan } from 'store/middleware/api/api';
import { estimateTypes, distributionTypes } from 'src/enums/tasks';
import { initialKpi } from 'store/entities/user-settings/user-settings';
import { v4 as uuidv4 } from 'uuid';
import { toast } from '@oliasoft-open-source/react-ui-library';
import i18n from 'i18next';
import translations from 'src/internationalisation/translation-map.json';
import { nextName } from 'client/common/utils/next-name';

export const initialRigOperationTemplateActivity = {
  rigOperationTemplateActivityId: '',
  name: 'New Activity',
  estimateType: estimateTypes.OPERATION_TIME,
  distribution: distributionTypes.PERT,
  min: 1,
  most: 2,
  max: 3,
};

export const initialRigOperationTemplate = {
  rigOperationTemplateId: '',
  name: 'New Template',
  activities: [],
};

const companySettings = createSlice({
  name: 'companySettings',
  initialState: {
    isAdding: false,
    isFetching: false,
    isUpdating: false,
    showTemplatesForm: false,
    showOperationsForm: false,
    showActivityForm: false,
    showDeleteOperationsModal: false,
    showDeleteActivitiesModal: false,
    showDeleteCostItemModal: false,
    showImportOperationModal: false,
    showDeleteCostCategoryModal: false,
    showKpiForm: false,
    showDeleteKpiModal: false,
    showAddOperationFromSectionsDataModal: false,
    showAddActivityFromSectionsDataModal: false,
    showCurrencyTemplateForm: false,
    showDeleteCurrencyTemplateModal: false,
    sectionTypes: [],
    settings: {
      currencies: { primaryCurrency: 'USD', additionalCurrencies: [] },
      kpi: [],
    },
    rigOperationTemplates: [initialRigOperationTemplate],
    activeRigOperationTemplateId: initialRigOperationTemplate.rigOperationTemplateId,
    currenciesList: [],
    currencyTemplate: [],
    currencyTemplatesFetching: false,
    companyCostSetup: {},
  },
  reducers: {
    companySettingsRequested: (companySettings) => {
      companySettings.isFetching = true;
      companySettings.settings = {};
    },
    companySettingsReceived: (companySettings, action) => {
      companySettings.isFetching = false;
      companySettings.settings = action.payload;
    },
    companySettingsRequestFailed: (companySettings) => {
      companySettings.isFetching = false;
    },
    companySettingsSaveRequested: (companySettings) => {
      companySettings.isAdding = true;
    },
    companySettingsSaveReceived: (companySettings) => {
      companySettings.isAdding = false;
    },
    companySettingsSaveFailed: (companySettings) => {
      companySettings.isAdding = false;
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.settings_errorAddingTemplate),
        },
      });
    },
    currenciesListRequested: (companySettings) => {
      companySettings.isFetching = true;
      companySettings.currenciesList = [];
    },
    currenciesListReceived: (companySettings, action) => {
      companySettings.isFetching = false;
      companySettings.currenciesList = action.payload.currencies;
    },
    currenciesListRequestFailed: (companySettings) => {
      companySettings.isFetching = false;
    },
    saveCurrenciesRequested: (companySettings, action) => {
      companySettings.isAdding = true;
      companySettings.settings.currencies.additionalCurrencies = action.payload.additionalCurrencies;
    },
    saveCurrenciesReceived: (companySettings) => {
      companySettings.isAdding = false;
    },
    saveCurrenciesRequestFailed: (companySettings) => {
      companySettings.isAdding = false;
    },
    saveTimeTrackerReceived: (companySettings) => {
      companySettings.isAdding = false;
    },
    saveTimeTrackerRequested: (companySettings, action) => {
      companySettings.isAdding = true;
      companySettings.settings.timeTracker = action.payload.timeTrackerSettings;
    },
    saveTimeTrackerRequestFailed: (companySettings) => {
      companySettings.isAdding = false;
    },
    kpiSaved: (companySettings, action) => {
      companySettings.isAdding = false;
      companySettings.showKpiForm = true;
      companySettings.settings = action.payload;
      companySettings.settings.kpi = companySettings.settings.kpi.map((item, i) => {
        item.active = i === companySettings.settings.kpi.length - 1;
        return item;
      });
    },
    companyKpiSelected: (companySettings, action) => {
      companySettings.settings.kpi = companySettings.settings.kpi.map((item) => {
        item.active = item.id === action.payload;
        return item;
      });
      companySettings.showKpiForm = true;
    },
    kpiRemoved: (companySettings, action) => {
      companySettings.settings = action.payload;
      companySettings.showKpiForm = false;
      companySettings.showDeleteKpiModal = false;
      companySettings.isAdding = false;
    },
    showKpiFormUpdated: (companySettings, action) => {
      companySettings.showKpiForm = action.payload;
    },
    showDeleteKpiModalUpdated: (companySettings, action) => {
      companySettings.showDeleteKpiModal = action.payload;
    },
    kpiUpdated: (companySettings, action) => {
      const { kpiIndex, kpi } = action.payload;
      companySettings.settings.kpi[kpiIndex] = kpi;
      companySettings.settings.kpi[kpiIndex].active = true;
    },

    rigOperationTemplatesItemSelected: (companySettings, action) => {
      companySettings.activeRigOperationTemplateId = action.payload;
    },
    rigOperationTemplatesRequested: (companySettings) => {
      companySettings.isFetching = true;
    },
    rigOperationTemplatesFetched: (companySettings, action) => {
      companySettings.isFetching = false;
      companySettings.rigOperationTemplates = action.payload.rigOperationTemplates;
    },
    rigOperationTemplatesRequestFailed: (companySettings) => {
      companySettings.isFetching = false;
    },
    rigOperationTemplatesAddRequested: (companySettings) => {
      companySettings.isAdding = true;
    },
    rigOperationTemplatesAddSaved: (companySettings, action) => {
      const {
        response: { rigOperationTemplateId },
        data: { ...addedTemplate },
      } = action.payload;
      companySettings.isAdding = false;
      companySettings.rigOperationTemplates.push({
        ...addedTemplate,
        rigOperationTemplateId,
      });
      companySettings.activeRigOperationTemplateId = rigOperationTemplateId;
    },
    rigOperationTemplatesAddRequestFailed: (companySettings, action) => {
      companySettings.isAdding = false;
      if (action.payload.errorMessages.includes('duplicate key value')) {
        toast({
          message: {
            type: 'Error',
            content: 'A template with this name already exists. Use a different name.',
          },
        });
      }
    },
    rigOperationTemplatesActivityAddRequested: (companySettings) => {
      companySettings.isAdding = true;
    },
    rigOperationTemplatesActivityAddSaved: (companySettings, action) => {
      companySettings.isAdding = false;
      const {
        data: { rigOperationTemplateId, ...newActivity },
        response: { rigOperationTemplateActivityId },
      } = action.payload;
      const template = companySettings.rigOperationTemplates.find(
        (template) => template.rigOperationTemplateId === rigOperationTemplateId,
      );
      if (template) {
        if (!template.activities) {
          template.activities = [];
        }
        template.activities.push({
          ...newActivity,
          rigOperationTemplateActivityId,
        });
      }
    },
    rigOperationTemplatesActivityAddRequestFailed: (companySettings) => {
      companySettings.isAdding = false;
    },
    rigOperationTemplateItemRemoved: (companySettings, action) => {
      const { rigOperationTemplateId } = action.payload;
      companySettings.rigOperationTemplates = companySettings.rigOperationTemplates.filter(
        (template) => template.rigOperationTemplateId !== rigOperationTemplateId,
      );
    },
    rigOperationTemplateActivityItemRemoved: (companySettings, action) => {
      const { rigOperationTemplateActivityId } = action.payload;
      companySettings.rigOperationTemplates.forEach((template) => {
        template.activities = template.activities.filter(
          (activity) => activity.rigOperationTemplateActivityId !== rigOperationTemplateActivityId,
        );
      });
    },
    rigOperationTemplateUpdateRequested: (companySettings) => {
      companySettings.isAdding = true;
    },
    rigOperationTemplateUpdateSaved: (companySettings, action) => {
      companySettings.isAdding = false;
      const updatedTemplate = action?.payload?.data;
      if (!updatedTemplate) {
        return;
      }
      const templateIndex = companySettings?.rigOperationTemplates?.findIndex(
        (template) => template?.rigOperationTemplateId === updatedTemplate.rigOperationTemplateId,
      );

      if (templateIndex === -1 || templateIndex === undefined) {
        return;
      }
      companySettings.rigOperationTemplates[templateIndex] = {
        ...companySettings.rigOperationTemplates[templateIndex],
        name: updatedTemplate.name ?? companySettings.rigOperationTemplates[templateIndex]?.name,
        activities: updatedTemplate.activities ?? [],
      };
    },
    rigOperationTemplateUpdateRequestFailed: (companySettings, action) => {
      companySettings.isAdding = false;
      if (action.payload.errorMessages.includes('duplicate key value')) {
        toast({
          message: {
            type: 'Error',
            content: 'A template with this name already exists. Use a different name.',
          },
        });
      }
    },
    rigOperationTemplateActivityUpdateRequested: (companySettings) => {
      companySettings.isAdding = true;
    },
    rigOperationTemplateActivityUpdateSaved: (companySettings, action) => {
      companySettings.isAdding = false;
      const updatedActivity = action.payload;
      const templateIndex = companySettings.rigOperationTemplates.findIndex(
        (template) => template.rigOperationTemplateId === updatedActivity.rigOperationTemplateId,
      );
      if (templateIndex !== -1) {
        const template = companySettings.rigOperationTemplates[templateIndex];
        const activityIndex = template.activities.findIndex(
          (activity) => activity.rigOperationTemplateActivityId === updatedActivity.rigOperationTemplateActivityId,
        );
        if (activityIndex !== -1) {
          template.activities[activityIndex] = {
            ...template.activities[activityIndex],
            ...updatedActivity,
          };
        }
      }
    },
    rigOperationTemplateActivityUpdateRequestFailed: (companySettings, action) => {
      companySettings.isAdding = false;
      toast({
        message: {
          type: 'Error',
          content: action.payload.errorMessages,
        },
      });
    },
    hierarchySettingsSaveRequested: (companySettings, action) => {
      companySettings.isUpdating = true;
      companySettings.settings = action.payload;
    },
    hierarchySettingsUpdated: (companySettings) => {
      companySettings.isUpdating = false;
    },
    currencyTemplatesRequested: (companySettings) => {
      companySettings.currencyTemplatesFetching = true;
    },
    currencyTemplatesFetched: (companySettings, action) => {
      companySettings.currencyTemplate = action.payload;
      companySettings.currencyTemplatesFetching = false;
    },
    currencyTemplatesRequestFailed: (companySettings) => {
      companySettings.isFetching = false;
    },
    currencyTemplatesSaveRequested: (companySettings) => {
      companySettings.isAdding = true;
    },
    currencyTemplateSaved: (companySettings, action) => {
      companySettings.isAdding = false;
      companySettings.showCurrencyTemplateForm = true;
      const savedCurrencyTempalte = { ...action.payload, active: true };
      const isCurrencyTemplateExist = companySettings.currencyTemplate.find(
        (item) => item.currencyTemplateId === savedCurrencyTempalte.currencyTemplateId,
      );
      if (isCurrencyTemplateExist) {
        companySettings.currencyTemplate = companySettings.currencyTemplate.map((item) => {
          const currentItem = savedCurrencyTempalte.isDefault ? { ...item, isDefault: false } : item;
          return item.currencyTemplateId === savedCurrencyTempalte.currencyTemplateId
            ? savedCurrencyTempalte
            : currentItem;
        });
      } else {
        companySettings.currencyTemplate = [...companySettings.currencyTemplate, savedCurrencyTempalte];
        companySettings.currencyTemplate = companySettings.currencyTemplate.map((item, i) => {
          item.active = i === companySettings.currencyTemplate.length - 1;
          return item;
        });
      }
    },
    currencyTemplateRemoved: (companySettings, action) => {
      const deletedCurrencyTemplateId = action.payload;
      companySettings.currencyTemplate = companySettings.currencyTemplate.filter(
        (item) => item.currencyTemplateId !== deletedCurrencyTemplateId,
      );
      companySettings.showCurrencyTemplateForm = false;
      companySettings.showDeleteCurrencyTemplateModal = false;
      companySettings.isAdding = false;
    },
    showCurrencyTemplateFormUpdated: (companySettings, action) => {
      companySettings.showCurrencyTemplateForm = action.payload;
    },
    showDeleteCurrencyTemplateModalUpdated: (companySettings, action) => {
      companySettings.showDeleteCurrencyTemplateModal = action.payload;
    },
    currencyTemplateUpdated: (companySettings, action) => {
      const { currencyTemplateIndex, currencyTemplate } = action.payload;
      companySettings.currencyTemplate[currencyTemplateIndex] = currencyTemplate;
      companySettings.currencyTemplate[currencyTemplateIndex].active = true;
    },
    currencyTemplateSelected: (companySettings, action) => {
      companySettings.currencyTemplate = companySettings.currencyTemplate.map((item) => {
        item.active = item.currencyTemplateId === action.payload;
        return item;
      });
      companySettings.showCurrencyTemplateForm = true;
    },
    currencyTemplateSaveRequestFailed: (companySettings, action) => {
      companySettings.isAdding = false;
      toast({
        message: {
          type: 'Error',
          content: action.payload.errorMessages,
        },
      });
    },
    companyCostSetupFetching: (companySettings) => {
      companySettings.isFetching = true;
    },
    companyCostSetupFetched: (companySettings, action) => {
      companySettings.isFetching = false;
      companySettings.companyCostSetup = action.payload;
    },
    companyCostSetupFetchFailed: (companySettings) => {
      companySettings.isFetching = false;
    },
  },
});

export const {
  companySettingsRequested,
  companySettingsReceived,
  companySettingsRequestFailed,
  companySettingsSaveRequested,
  companySettingsSaveReceived,
  companySettingsSaveFailed,
  currenciesListRequested,
  currenciesListReceived,
  currenciesListRequestFailed,
  saveCurrenciesRequested,
  saveCurrenciesRequestFailed,
  saveCurrenciesReceived,
  saveTimeTrackerRequested,
  saveTimeTrackerRequestFailed,
  saveTimeTrackerReceived,
  kpiSaved,
  companyKpiSelected,
  kpiRemoved,
  showKpiFormUpdated,
  showDeleteKpiModalUpdated,
  kpiUpdated,
  rigOperationTemplatesRequested,
  rigOperationTemplatesFetched,
  rigOperationTemplatesRequestFailed,
  rigOperationTemplatesAddRequested,
  rigOperationTemplatesAddSaved,
  rigOperationTemplatesAddRequestFailed,
  rigOperationTemplatesItemSelected,
  rigOperationTemplatesActivityAddRequested,
  rigOperationTemplatesActivityAddSaved,
  rigOperationTemplatesActivityAddRequestFailed,
  rigOperationTemplateItemRemoved,
  rigOperationTemplateActivityItemRemoved,
  rigOperationTemplateUpdateSaved,
  rigOperationTemplateUpdateRequested,
  rigOperationTemplateUpdateRequestFailed,
  rigOperationTemplateActivityUpdateRequested,
  rigOperationTemplateActivityUpdateSaved,
  rigOperationTemplateActivityUpdateRequestFailed,
  hierarchySettingsSaveRequested,
  hierarchySettingsUpdated,
  currencyTemplatesRequested,
  currencyTemplatesFetched,
  currencyTemplatesRequestFailed,
  currencyTemplateSaved,
  currencyTemplateRemoved,
  showCurrencyTemplateFormUpdated,
  showDeleteCurrencyTemplateModalUpdated,
  currencyTemplateUpdated,
  currencyTemplateSelected,
  currencyTemplatesSaveRequested,
  currencyTemplateSaveRequestFailed,
  companyCostSetupFetching,
  companyCostSetupFetched,
  companyCostSetupFetchFailed,
} = companySettings.actions;
export default companySettings.reducer;

/**
 * Get company settings
 *
 * @param companyId
 */
export const getCompanySettings = (companyId) =>
  apiCallBegan({
    url: `/api/settings/company/${companyId}`,
    method: 'GET',
    onStart: companySettingsRequested.type,
    onSuccess: companySettingsReceived.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.settings_failedToGetCompanySettings),
        },
      });
      return { type: companySettingsRequestFailed.type };
    },
  });

/**
 *  Get currencies list
 */
export const getCurrencies = () =>
  apiCallBegan({
    url: '/api/currencies',
    method: 'GET',
    onStart: currenciesListRequested.type,
    onSuccess: currenciesListReceived.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.settings_failedToGetCurrencies),
        },
      });
      return { type: currenciesListRequestFailed.type };
    },
  });

/**
 * Save currencies
 *
 * @param companyId
 * @param primaryCurrency
 * @param additionalCurrencies
 * @param settings
 */
export const saveCurrencies = (companyId, primaryCurrency, additionalCurrencies, settings) => {
  const data = cloneDeep(settings);
  data.currencies.primaryCurrency = primaryCurrency;
  data.currencies.additionalCurrencies = additionalCurrencies;

  return apiCallBegan({
    url: `/api/settings/company/${companyId}`,
    method: 'POST',
    data,
    onStart: () => ({
      type: saveCurrenciesRequested.type,
      payload: { additionalCurrencies },
    }),
    onSuccess: saveCurrenciesReceived.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.settings_failedToSaveCurrencies),
        },
      });
      return { type: saveCurrenciesRequestFailed.type };
    },
  });
};

/**
 * update time tracker settings
 *
 * @param companyId
 * @param timeTrackerSettings
 * @param settings
 */
export const saveTimeTracker = (companyId, timeTrackerSettings, settings) => {
  const data = cloneDeep(settings);
  data.timeTracker = timeTrackerSettings;

  return apiCallBegan({
    url: `/api/settings/company/${companyId}`,
    method: 'POST',
    data,
    onStart: () => ({
      type: saveTimeTrackerRequested.type,
      payload: { timeTrackerSettings },
    }),
    onSuccess: saveTimeTrackerReceived.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.settings_failedToSaveTimeTracker),
        },
      });
      return { type: saveTimeTrackerRequestFailed.type };
    },
  });
};

/**
 * Adds an initial Key Performance Indicator (KPI) to the company settings.
 *
 * @param {string} companyId
 * @param {string} name - The name of the KPI.
 * @param {object} settings - The user settings.
 * @returns {Object} The API call action object.
 */
export const addInitialCompanyKpi = (name, settings, companyId) => {
  const data = cloneDeep(settings);
  const { kpi: kpis } = data;
  const newName = nextName(
    kpis.map((kpi) => kpi.name),
    name,
  );

  const kpi = {
    ...initialKpi,
    name: newName,
    id: uuidv4(),
  };
  data.kpi = [...data.kpi, kpi];

  data.kpi = data.kpi.map(({ active, ...rest }) => rest);

  return apiCallBegan({
    url: `/api/settings/company/${companyId}`,
    method: 'POST',
    data,
    onStart: companySettingsSaveRequested.type,
    onSuccess: (response) => ({
      type: kpiSaved.type,
      payload: response,
    }),
    onError: companySettingsSaveFailed.type,
  });
};

/**
 * Removes a KPI from company settings.
 *
 * @param {string} companyId
 * @param {string} id - The ID of the KPI to be removed.
 * @param {Object} settings - The user settings object.
 * @returns {Object} The API call action object.
 */
export const removeCompanyKpi = (id, settings, companyId) => {
  const data = cloneDeep(settings);
  const index = data.kpi.findIndex((item) => item.id === id);
  data.kpi.splice(index, 1);

  return apiCallBegan({
    url: `/api/settings/company/${companyId}`,
    method: 'POST',
    data,
    onStart: companySettingsSaveRequested.type,
    onSuccess: (response) => ({
      type: kpiRemoved.type,
      payload: response,
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.settings_failedToRemove),
        },
      });
      return { type: companySettingsSaveFailed.type };
    },
  });
};

/**
 * Updates the KPI with the given value and sends an API request to update the settings.
 *
 * @param {string} companyId
 * @param {Object} kpi - The new KPI value to update.
 * @param {Object} settings - The current settings object to update.
 * @returns {Object} The API call action object.
 */
export const updateCompanyKpi = (kpi, settings, companyId) => {
  const data = cloneDeep(settings);
  const kpiIndex = data.kpi.findIndex((item) => item.active);

  if (data.kpi.some((k) => k.name.toUpperCase().trim() === kpi.name.toUpperCase().trim() && !k.active)) {
    toast({
      message: {
        type: 'Error',
        heading: i18n.t(translations.settings_failedToUpdate),
        content: i18n.t(translations.settings_nameMustBeUnique, {
          type: i18n.t(translations.kpi),
          name: kpi.name,
        }),
      },
    });
    kpi.name = data.kpi[kpiIndex].name;
  }
  data.kpi[kpiIndex] = kpi;

  return apiCallBegan({
    url: `/api/settings/company/${companyId}`,
    method: 'POST',
    data,
    onSuccess: () => ({
      type: kpiUpdated.type,
      payload: { kpiIndex, kpi },
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.settings_failedToUpdate),
        },
      });
    },
  });
};

/**
 * Update hierarchy settings.
 *
 * @param {object} updatedSettings - The settings data.
 * @param {string} companyId - The company ID.
 * @returns {object} - The API call result.
 */

export const updateHierarchySettings = (updatedSettings, companyId) => {
  const data = cloneDeep(updatedSettings);
  return apiCallBegan({
    url: `/api/settings/company/${companyId}`,
    method: 'POST',
    data,
    onStart: () => ({
      type: hierarchySettingsSaveRequested.type,
      payload: data,
    }),
    onSuccess: hierarchySettingsUpdated.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.settings_failedToAdd),
        },
      });
      return { type: hierarchySettingsUpdated.type };
    },
  });
};

/**
 * Get all rig operation templates
 *
 * @param {object} data
 */

export const getAllRigOperationTemplates = () => {
  return apiCallBegan({
    url: `/api/rig-operation`,
    method: 'GET',
    onStart: () => ({
      type: rigOperationTemplatesRequested.type,
    }),
    onSuccess: (response) => ({
      type: rigOperationTemplatesFetched.type,
      payload: response,
    }),
    onError: rigOperationTemplatesRequestFailed.type,
  });
};

/**
 * Add rig operation template
 *
 * @param {object} data
 */

export const addRigOperationTemplate = (data) => {
  return apiCallBegan({
    url: `/api/rig-operation`,
    method: 'POST',
    data,
    onStart: () => {
      return {
        type: rigOperationTemplatesAddRequested.type,
      };
    },
    onSuccess: (response) => ({
      type: rigOperationTemplatesAddSaved.type,
      payload: { response, data },
    }),
    onError: (error) => ({
      type: rigOperationTemplatesAddRequestFailed.type,
      payload: error,
    }),
  });
};
/**
 * Add initial rig operation template activity
 *
 * @param {object} data
 */

export const addRigOperationTemplateActivity = (data) => {
  return apiCallBegan({
    url: `/api/rig-operation-activity`,
    method: 'POST',
    data,
    onStart: () => {
      return {
        type: rigOperationTemplatesActivityAddRequested.type,
      };
    },
    onSuccess: (response) => ({
      type: rigOperationTemplatesActivityAddSaved.type,
      payload: { response, data },
    }),
    onError: rigOperationTemplatesActivityAddRequestFailed.type,
  });
};

/**
 * Remove a rig operation template
 *
 * @param {string} id
 */

export const removeRigOperationTemplateItem = (id) => {
  return apiCallBegan({
    url: `/api/rig-operation/${id}`,
    method: 'DELETE',
    onSuccess: rigOperationTemplateItemRemoved.type,
  });
};

/**
 * Remove an activity from a rig operation template
 *
 * @param {string} id
 */
export const removeRigOperationTemplateActivityItem = (id) => {
  return apiCallBegan({
    url: `/api/rig-operation-activity/${id}`,
    method: 'DELETE',
    onSuccess: rigOperationTemplateActivityItemRemoved.type,
  });
};

/**
 * update a rig operation template item
 *
 * @param {string} id
 */
export const updateRigOperationTemplateItem = (data) => {
  const id = data.rigOperationTemplateId;
  return apiCallBegan({
    url: `/api/rig-operation/${id}`,
    method: 'PUT',
    data,
    onStart: rigOperationTemplateUpdateRequested.type,
    onSuccess: (response) => ({
      type: rigOperationTemplateUpdateSaved.type,
      payload: { response, data },
    }),
    onError: (error) => ({
      type: rigOperationTemplateUpdateRequestFailed.type,
      payload: error,
    }),
  });
};

/**
 * update a rig operation template activity item
 *
 * @param {string} id
 */

export const updateRigOperationTemplateActivityItem = (data) => {
  const id = data.rigOperationTemplateActivityId;
  return apiCallBegan({
    url: `/api/rig-operation-activity/${id}`,
    method: 'PUT',
    data,
    onStart: rigOperationTemplateActivityUpdateRequested.type,
    onSuccess: (response) => ({
      type: rigOperationTemplateActivityUpdateSaved.type,
      payload: response,
    }),
    onError: (response) => ({
      type: rigOperationTemplateActivityUpdateRequestFailed.type,
      payload: response,
    }),
  });
};

export const addInitialRigOperationTemplateActivity = (rigOperationTemplateId, existingActivities) => {
  // Extract existing names
  const existingNames = existingActivities.map((activity) => activity.name);

  // Generate a unique name for the new activity
  const uniqueName = nextName(existingNames, 'New Activity');

  // Create the new activity
  const newActivity = {
    ...cloneDeep(initialRigOperationTemplateActivity),
    name: uniqueName,
    rigOperationTemplateActivityId: uuidv4(),
    rigOperationTemplateId,
    // companyId,
  };

  return apiCallBegan({
    url: `/api/rig-operation-activity`,
    method: 'POST',
    data: newActivity,
    onStart: rigOperationTemplatesActivityAddRequested.type,
    onSuccess: (response) => ({
      type: rigOperationTemplatesActivityAddSaved.type,
      payload: { response, data: newActivity },
    }),
    onError: rigOperationTemplatesActivityAddRequestFailed.type,
  });
};

/**
 * Get all company currency templates
 *
 */
export const getCompanyCurrencyTemplates = () => {
  return apiCallBegan({
    url: `/api/currency-template`,
    method: 'GET',
    onStart: currencyTemplatesRequested.type,
    onSuccess: currencyTemplatesFetched.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: 'Failed to fetch currency templates',
        },
      });
      return { type: currencyTemplatesRequestFailed.type };
    },
  });
};

/**
 * Adds an initial Currency Template to the company settings
 *
 * @param {string} companyId
 * @param {string} primaryCurrency
 * @param {Array} additionalCurrencies
 * @param {string} name
 * @returns {Object} The API call action object.
 */
export const addInitialCurrencyTemplate = (
  companyId,
  primaryCurrency,
  additionalCurrencies = [],
  currencyTemplateList,
) => {
  const existingNames = currencyTemplateList.map((currencyTemplate) => currencyTemplate.name);
  const uniqueName = nextName(existingNames, 'New Currency Template');

  const newCurrencyTemplate = {
    name: uniqueName,
    primaryCurrency,
    additionalCurrencies,
    companyId,
  };

  return apiCallBegan({
    url: `/api/currency-template`,
    method: 'POST',
    data: newCurrencyTemplate,
    onStart: currencyTemplatesSaveRequested.type,
    onSuccess: currencyTemplateSaved.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: 'Failed to save currency template',
        },
      });
      return { type: currencyTemplateSaveRequestFailed.type };
    },
  });
};

/**
 * Remove a currency template
 *
 * @param {string} currencyTemplateId
 */

export const removeCurrencyTemplate = (currencyTemplateId) => {
  return apiCallBegan({
    url: `/api/currency-template/${currencyTemplateId}`,
    method: 'DELETE',
    onSuccess: currencyTemplateRemoved.type,
  });
};

/**
 * Update a currency template
 *
 * @param {object} currencyTemplate
 */

export const updateCurrencyTemplate = (currencyTemplate) => {
  const id = currencyTemplate.currencyTemplateId;
  return apiCallBegan({
    url: `/api/currency-template/${id}`,
    method: 'PUT',
    data: currencyTemplate,
    onStart: currencyTemplatesSaveRequested.type,
    onSuccess: (response) => ({
      type: currencyTemplateSaved.type,
      payload: response,
    }),
    onError: (response) => ({
      type: currencyTemplateSaveRequestFailed.type,
      payload: response,
    }),
  });
};

export const getCompanyCostSetup = (companyId) => {
  return apiCallBegan({
    url: `/api/company-cost-setup/${companyId}`,
    method: 'GET',
    onStart: companyCostSetupFetching,
    onSuccess: (costSetupData) => ({
      type: companyCostSetupFetched.type,
      payload: costSetupData,
    }),
    onError: companyCostSetupFetchFailed.type,
  });
};

export const updateCompanyCostSetup = (companyId, costSetupData) => {
  return apiCallBegan({
    url: `/api/company-cost-setup/${companyId}`,
    method: 'PUT',
    data: costSetupData,
    onStart: companyCostSetupFetching.type,
    onSuccess: (costSetupData) => ({
      type: companyCostSetupFetched.type,
      payload: costSetupData,
    }),
    onError: companyCostSetupFetchFailed.type,
  });
};
